"use client"
import type { ChainIdentifier } from "@opensea/graphql"
import { isAddressEqual } from "@opensea/utils"
import { useFlag, useVariant } from "@unleash/nextjs/client"
import { useAddress } from "@/hooks/wallet"

export function useIsCollectionOverviewOnlyEnabled(slug: string) {
  const { enabled, payload, name } = useVariant("show_collection_overview_only")
  return (
    enabled &&
    name === "collection_slugs" &&
    payload?.value.split(",").includes(slug)
  )
}

export function useIsWagmiStateTrackingEnabled() {
  return useFlag("wagmi_state_tracking")
}

export function useCoinbaseOneBannerEnabled() {
  return useFlag("coinbase_one_banner_enabled")
}

export function useIsShipmentBannerEnabled() {
  return useFlag("shipment_banner_enabled")
}

export function useIsRewardsKillSwitchEnabled() {
  // If enabled, we will not show rewards at all
  return useFlag("reward_kill_switch_enabled")
}

export function useIsRewardsMaintananceEnabled() {
  // If enabled, we will show maintanance message for rewards
  return useFlag("is_rewards_maintanance")
}

export function useIsRewardPollingEnabled() {
  return useFlag("reward_polling_enabled")
}

export function useIsWeekendVoyageBannerEnabled() {
  return useFlag("weekend_voyage_banner_enabled")
}

export function useIsChestReminderBannerEnabled() {
  return useFlag("chest_reminder_banner_enabled")
}

export function useIsProfileTokenActivityEnabled() {
  return useFlag("profile_token_activity_enabled")
}

export function useIsWalletPositionStatsEnabled() {
  return useFlag("is_wallet_position_stats_enabled")
}

export function useIsPortfolioGainColumnEnabled() {
  return useFlag("portfolio_gain_column")
}

export function useIsProfileTokensPaginationEnabled() {
  return useFlag("profile_tokens_pagination_enabled")
}
/**
 * Checks if the voyage scroll indicator feature is enabled.
 * Uses the same feature flag as useGetVoyageId() for unified control.
 */
export function useIsVoyageScrollIndicatorEnabled() {
  return useFlag("set_token_merchandising_rule_progress")
}

/**
 * Gets the specific voyage ID for the scroll indicator from the feature flag payload.
 * Uses the same feature flag as useIsVoyageScrollIndicatorEnabled() - when enabled,
 * the payload.value contains the target voyage ID.
 */
export function useGetVoyageId() {
  const { enabled, payload } = useVariant(
    "set_token_merchandising_rule_progress",
  )
  return enabled && payload && payload?.value
}

export function useIsMCPDeveloperAccessEnabled() {
  return useFlag("mcp_enabled")
}

export function useCurrencyActivityEnabled() {
  return !useFlag("currency_activity_disabled")
}

export function useIs1155WithTokenWaves(slug: string) {
  const { enabled, payload, name } = useVariant("is_1155_with_token_waves")
  return enabled && name === "slugs" && payload?.value.split(",").includes(slug)
}

export function useIsYourTradesTokenActivityEnabled() {
  return useFlag("show_your_trades_token_activity")
}

export function useIsCurrencyChartAnnotationEnabled() {
  return useFlag("currency_chart_annotation_enabled")
}

export function useIsCurrencyChartCandleSizeEnabled() {
  return useFlag("currency_chart_candle_size_enabled")
}

export function useIsChartInteractivityEnabled() {
  return useFlag("chart_interactivity_enabled")
}

export function useIsAllTokenTradingBoostBannerEnabled() {
  return useFlag("all_token_trading_boost_banner_enabled")
}

export function useIsSolanaProfileActivityEnabled() {
  return useFlag("solana_profile_token_activity_fe_enabled")
}

export function useIsUsdSwapInputEnabled({
  chain,
  contractAddress,
}: {
  chain: ChainIdentifier
  contractAddress: string
}) {
  const { enabled, payload, name } = useVariant("enable_swap_usd_input")

  if (!enabled) {
    return false
  }

  if (name === "disabled_tokens") {
    const disabledTokens = payload?.value.split(",")
    return !disabledTokens?.includes(`${chain}:${contractAddress}`)
  }

  return true
}

export function useIsNFTStrategyCurrencySelectionDisabled() {
  return useFlag("disable_nftstrategy_currency_selection")
}

export function useIsWalletWarningBannerEnabled() {
  return useFlag("wallet_warning_banner_enabled")
}

export function useIsSwapV2Enabled() {
  return useFlag("enable_swap_v2")
}

export function useOutageBanner() {
  const { enabled, payload } = useVariant("outage_banner")
  return {
    enabled,
    message: payload?.value as string | undefined,
  }
}

export function useIsTokenWatchlistEnabled() {
  return useFlag("token_watchlist_enabled")
}

export function useIsMultiTraitOfferEnabled() {
  return useFlag("multi_trait_offers_enabled")
}

export function useIsHalloweenLogoEnabled() {
  return useFlag("halloween_logo_enabled")
}

export function useIsTreasureChestOpenableOverrideEnabled() {
  const address = useAddress()
  const { enabled, payload } = useVariant(
    "treasure_chest_openable_override_enabled",
  )

  if (!enabled) {
    return false
  }

  if (!address) {
    return false
  }

  if (!payload?.value) {
    return false
  }

  try {
    const addresses = JSON.parse(payload.value) as string[]
    return (
      Array.isArray(addresses) &&
      addresses.some(a => isAddressEqual(a, address))
    )
  } catch {
    return false
  }
}

export function useIsTreasureChestOpenable(isOpenable: boolean) {
  const overrideEnabled = useIsTreasureChestOpenableOverrideEnabled()
  return isOpenable || overrideEnabled
}

export function useIsMarketingEligibilityDropdownEnabled() {
  return useFlag("marketing_eligibility_dropdown_enabled")
}
